<template>
  <div id="kt_header" class="header align-items-stretch">
    <div class="container-fluid d-flex align-items-stretch justify-content-between">

      <!-- Mobile menu toggle -->
      <div class="d-flex align-items-center d-lg-none ms-n2 me-2" title="Show aside menu">
        <div class="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px" id="kt_aside_mobile_toggle">
          <i class="bi bi-list fs-1"></i>
        </div>
      </div>

      <!-- Logo mobile -->
      <div class="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
        <a href="#" class="d-lg-none">
          <img alt="Logo" src="/assets/media/logos/qomerxia-logo.png" class="h-15px" />
        </a>
      </div>

      <!-- Header content -->
      <div class="d-flex align-items-stretch justify-content-between flex-lg-grow-1">

        <!-- Left side: Interface de vente -->
        <div class="d-flex align-items-center" id="kt_header_nav">
          <button
            v-if="$canInActiveShop('boutique_sale') && $hasModule('pos')"
            @click.prevent="openPosInterface"
            class="btn btn-light-primary shadow-sm d-inline-flex align-items-center gap-2"
            :title="$t('sell_interface')"
          >
            <i class="bi bi-calculator-fill"></i>
            <span class="d-none d-md-inline">{{ $t('sell_interface') }}</span>
          </button>
        </div>

        <!-- Right side: Actions -->
        <div class="d-flex align-items-center gap-2">

          <!-- Shop switcher (société multi-boutique) -->
          <div class="header-item" v-if="hasMultipleShops">
            <div
              class="btn btn-icon-muted btn-active-light btn-active-color-primary h-30px h-md-40px px-3 d-flex align-items-center gap-2"
              data-kt-menu-trigger="hover" data-kt-menu-attach="parent" data-kt-menu-placement="bottom-end">
              <i class="bi bi-shop fs-5"></i>
              <span class="d-none d-md-block shop-switcher-name">{{ currentShop?.name }}</span>
              <i class="bi bi-chevron-down fs-8"></i>
            </div>
            <div class="menu menu-sub menu-sub-dropdown menu-column w-250px" data-kt-menu="true">
              <div class="menu-item px-3" v-for="shop in getSocietyShops" :key="shop.id">
                <a @click.prevent="doSwitchShop(shop)" href="#" class="menu-link d-flex align-items-center justify-content-between px-5">
                  <span>{{ shop.name }}</span>
                  <i v-if="shop.id === currentUser?.shop_id" class="bi bi-check-lg text-primary"></i>
                </a>
              </div>
            </div>
          </div>

          <!-- « Premiers pas » : dans le panneau d'aide seulement (plus de bouton permanent ici) -->

          <!-- Aide contextuelle : guides de la page en cours -->
          <div class="header-item">
            <button type="button" :title="$t('help.title')" @click="openHelp()"
              class="btn btn-icon btn-light-primary w-30px h-30px w-md-40px h-md-40px">
              <i class="bi bi-question-lg fs-2"></i>
            </button>
          </div>

          <!-- Support tickets -->
          <div class="header-item">
            <router-link :to="{ name: 'my-tickets-path' }" :title="$t('support.title')"
              class="btn btn-icon btn-light-primary w-30px h-30px w-md-40px h-md-40px position-relative">
              <i class="bi bi-headset fs-2"></i>
            </router-link>
          </div>

          <!-- Language selector -->
          <div class="header-item">
            <div
              class="btn btn-icon btn-icon-muted btn-active-light btn-active-color-primary w-30px h-30px w-md-40px h-md-40px"
              data-kt-menu-trigger="hover" data-kt-menu-attach="parent" data-kt-menu-placement="bottom-end">
              <img class="h-20px w-20px rounded-sm" :src="currentLanguage?.img" alt="" />
            </div>
            <div class="menu menu-sub menu-sub-dropdown menu-column w-150px" data-kt-menu="true">
              <div v-for="lang in langs" :key="lang.code" class="menu-item px-3">
                <a @click.prevent="changeLang(lang)" href="#" class="menu-link d-flex px-5 active">
                  <span class="symbol symbol-20px me-4">
                    <img class="rounded-1" :src="lang.img" :alt="$t(lang.title)" />
                  </span>{{ $t(lang.title) }}
                </a>
              </div>
            </div>
          </div>

          <!-- Installer l'application (si possible et pas déjà installée) -->
          <PwaInstallButton />

          <!-- Notifications -->
          <div class="d-flex align-items-center" id="kt_drawer_chat_toggle">
            <div class="btn btn-icon btn-light-primary shadow-sm w-30px h-30px w-md-40px h-md-40px position-relative">
              <i class="bi bi-bell fs-2"></i>
              <span v-if="numberNotification > 0" class="notif-badge">
                {{ numberNotification > 99 ? '99+' : numberNotification }}
              </span>
            </div>
          </div>

          <!-- User profile -->
          <div class="d-flex align-items-center">
            <router-link :to="{ name: 'settings-profile' }" id="step-profil"
              class="btn btn-light-primary shadow-sm h-30px h-md-40px px-3 d-flex align-items-center gap-2">
              <span class="fw-bold fs-7">{{ userNameInitial }}</span>
              <span class="user-name d-none d-md-block">{{ userName }}</span>
            </router-link>
          </div>

          <!-- Logout -->
          <div class="d-flex align-items-center">
            <span @click.prevent="log_out" :title="$t('header.logout')"
              class="btn btn-icon btn-dark shadow-sm w-30px h-30px w-md-40px h-md-40px">
              <i class="bi bi-box-arrow-right fs-2"></i>
            </span>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapActions } from 'vuex'
import { forgetCachedBranding } from '@/composables/useBranding'
import PwaInstallButton from '@/components/PwaInstallButton.vue'
import { openHelp } from '@/help/helpState'

export default {
  components: { PwaInstallButton },
  data() {
    return {
      langs: {
        en: {
          'img': '/assets/media/flags/united-states.svg',
          'title': 'header.language.english',
          'code': 'en'
        },
        fr: {
          'img': '/assets/media/flags/france.svg',
          'title': 'header.language.french',
          'code': 'fr'
        },
      },
      currency: [],
    }
  },
  props: {
    numberNotification: { required: false, type: Number, default: 0 },
  },
  async mounted(){
    this.initCurrency()
  },
  watch: {
    // currentUser est chargé par le mounted() du LayoutBasic parent, qui
    // s'exécute APRÈS le mounted() de ce composant enfant (ordre Vue) — un
    // fetch ponctuel ici manquerait toujours society_id au premier rendu.
    'currentUser.society_id': {
      immediate: true,
      handler(societyId) {
        if (societyId) {
          this.fetchSociety()
        }
      },
    },
  },
  methods: {
    openHelp,
    ...mapActions('auth', [
      'logout'
    ]),
    ...mapActions('user', [
      'changeLanguage'
    ]),
    ...mapActions('permission', [
      'loadPermissions'
    ]),
    ...mapActions('shop', ['getCurrency']),
    ...mapActions('society', ['fetchSociety', 'switchShop']),

    async doSwitchShop(shop) {
      if (shop.id === this.currentUser?.shop_id) return
      try {
        await this.switchShop(shop.id)
        // les couleurs gardées sont celles de l'ancienne boutique
        forgetCachedBranding()
        window.location.reload()
      } catch (err) {
        // toast déjà affiché par l'intercepteur axios global (main.js)
      }
    },
    async initCurrency(){
      let response = await this.getCurrency()
      this.currency = response.data
    },
    changeLang(lang) {
      this.$i18n.locale = lang.code
      localStorage.setItem('locale', lang.code)
      this.changeLanguage(lang.code)
      this.$nextTick(() => {
        $('body .kt_select').select2()
        $('body .kt_select').select2().select2()
        $('body .kt_select2').select2().select2()
        $('body .kt_select2').select2().select2()
      })
    },

    reloadPage() {
      this.$router.push({ path: this.$route.path });
      window.history.go(0);
    },

    openPosInterface() {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches
      const isMobile = window.innerWidth <= 767
      if (isPWA || isMobile) {
        this.$router.push({ name: 'pos-caisse-path' })
      } else {
        const url = this.$router.resolve({ name: 'pos-caisse-path' }).href
        window.open(url, '_blank')
      }
    },

    async log_out() {
      Swal.fire({
        text: i18n.global.t('swal.logout'),
        icon: "success",
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: i18n.global.t('swal.yes'),
        cancelButtonText: i18n.global.t('swal.no'),
        customClass: {
          confirmButton: "btn fw-bold btn-primary",
          cancelButton: "btn fw-bold btn-secondary"
        }
      }).then(async (result) => {
        if (result.value) {
          this.logout()
          window.toastr['success'](i18n.global.t("notification.logout"))
        }
      });
    },
  },
  updated() {
    this.$nextTick(() => {
      KTMenu.init();
    })
  },
  computed: {
    ...mapGetters('user', [
      'currentUser'
    ]),
    ...mapGetters('society', ['getSocietyShops']),

    hasMultipleShops() {
      return this.getSocietyShops.length > 1
    },

    currentShop() {
      return this.getSocietyShops.find(shop => shop.id === this.currentUser?.shop_id)
    },

    userName() {
      return this.currentUser ? this.currentUser.fullname : ""
    },

    userNameInitial() {
      if (this.userName) {
        let splitUserName = this.userName.split(" ")
        return splitUserName[0].charAt(0).toUpperCase() + (splitUserName.length > 1 ? splitUserName[1].charAt(0).toUpperCase() : "")
      }
      return ''
    },

    currentLanguage() {
      return this.langs[this.$i18n?.locale]
    },
  },
}
</script>

<style scoped>
/* Badge notifications */
.notif-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #f1416c;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: notif-blink 1.2s ease-in-out infinite;
}

@keyframes notif-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.85); }
}

.shop-switcher-name {
  font-size: 0.875rem;
  font-weight: 500;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 500;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
